import styled, { css, keyframes } from "styled-components";
import { darken, rgba } from "polished";
import { Root as CheckboxRoot, Indicator as CheckboxIndicator, } from "@radix-ui/react-checkbox";

const removeAnimate = keyframes`
  0%{}
  20%{ opacity: 1; height: 51px;  overflow: hidden;  transform: translateX(0);  }
  70%{ opacity: 0; }
  90%{ transform: translateX(-105%); }
  100%{ height: 0px; border: none}
`;

const returnAnimate = keyframes`
  0%{ opacity: 0; height: 0px; transform: translateX(-105%);  }
  20%{ opacity: 0;}
  70%{ transform: translateX(0); height: 51px;  border-bottom: 1px solid ${(props) => props.theme.colors.gray[300]}; }
  90%{ opacity: 1; pointer-events: initial }
  100%{}
`;

export const TableConfigItem = styled.p`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin: 0;
  list-style: none;
`;

export const TableConfigCheckIndicator = styled(CheckboxIndicator)`
  transition: all ${(props) => props.theme.animation.delay.fast} ease;
  background: ${(props) => props.theme.colors.scale[500]};
  border-radius: 3px;
  width: 70%;
  height: 70%;
`;

export const TableConfigCheck = styled(CheckboxRoot)`
  border: 1px solid ${(props) => props.theme.colors.gray[100]};
  background: ${(props) => props.theme.colors.background};
  outline: none;
  padding: 0;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px ${(props) => rgba(props.theme.colors.black, 0.2)};
  transition: all ${(props) => props.theme.animation.delay.fast} ease;
  cursor: pointer;
  &:hover {
    border-color: ${(props) => props.theme.colors.scale[500]};
  }
  &:disabled{
    cursor: default;
    pointer-events: none;
    ${TableConfigCheckIndicator}{
      background: ${(props) => props.theme.colors.gray[300]};
    }
  }
`;


export const TableConfigLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => props.theme.colors.gray[800]};
`;

export const TableLine = styled.div`
  --tableLine-bg-hover-color: ${(props) => props.theme.colors.gray[100]};

  ${(props) => props.type === "outflow" && css`
      --tableLine-bg-hover-color: ${(props) =>
      rgba(props.theme.colors.error, 0.15)};
  `}

  border-bottom: 1px solid ${(props) => props.theme.colors.gray[300]};
  display: grid;
  align-items: center;
  gap: 3px;
  /* mesma largura do cabecalho, para as colunas continuarem alinhadas
     quando a tabela rola na horizontal */
  min-width: max-content;
  grid-template-columns: ${(props) => props.columns};
  transition: all ${(props) => props.theme.animation.delay.slow} ease;
  font-size: 14px;
  &:hover {
    background: var(--tableLine-bg-hover-color);
  }
  &:last-child {
    border: none;
  }

  ${(props) => props.removed && css`
      overflow: hidden;
      pointer-events: none;
      minheight: 51px;
      background: ${(props) => rgba(props.theme.colors.error, 0.3)};
      animation: ${removeAnimate} ${(props) => props.theme.animation.delay.slow}
        ease-in-out forwards;
  `};
  ${(props) => props.returned && css`
      overflow: hidden;
      background: transparent;
      animation: ${returnAnimate} ${(props) => props.theme.animation.delay.slow}
        ease-in-out forwards;
   `};
  ${(props) => !props.returned && !props.removed && css`
      opacity: 1;
      min-height: 51px;
      /*
       * Nao ha transform aqui de proposito. Era translateX(0%) — inocuo
       * visualmente, mas transform cria CONTEXTO DE EMPILHAMENTO: o menu de
       * acoes (z-index 20) ficava preso dentro da propria linha e as linhas
       * seguintes pintavam por cima dele. O menu aparecia na arvore, com
       * overflow liberado, e mesmo assim nao dava para clicar em "Vender".
       * As animacoes de remover/devolver continuam usando transform nos seus
       * proprios ramos, onde ele e necessario.
       */
  `};
`;

export const TableContent = styled.div`
  display: flex;
  flex-direction: column;
  /* acompanha a largura rolada como o cabecalho e as linhas; sem isto a borda
     direita ficava na largura visivel e riscava o meio da tabela */
  min-width: max-content;
  border: 1px solid ${(props) => props.theme.colors.gray[300]};
  border-top: none;
  color: ${(props) => props.theme.colors.gray[800]};
  border-radius: 0 0 3px 3px;
`;

export const TableItem = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;
  padding: 5px 0px;
  /* sem isto a celula cresce com o conteudo e estoura a coluna do grid */
  min-width: 0;
  overflow: hidden;

  /* celula com componente dentro (menu de acoes, badge) nao recorta: o
     overflow existe para o texto nao estourar a coluna, mas estava cortando
     o menu dos tres pontinhos inteiro */
  &[data-livre="true"] {
    overflow: visible;
    padding: 0px 10px 0px 15px;
  }
  & > span {
    padding: 0px 10px 0px 15px;
    width: 100%;
    /* nome gigante vira "Maria Aparecida de..." em vez de quebrar a linha */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${(props) =>
    props.wrap &&
    css`
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
      `}
    ${(props) =>
    props.type === "inflow" &&
    css`
        color: ${(props) => darken(0.2, props.theme.colors.success)};
      `}
    ${(props) =>
    props.type === "outflow" &&
    css`
        color: ${(props) => props.theme.colors.error};
        padding-left: 7px;
        &::before {
          content: "- ";
        }
      `}
  }
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  background: ${(props) => props.theme.colors.scale[500]};
  transition: all ${(props) => props.theme.animation.delay.slow} ease;
  gap: 3px;
  border-radius: 5px 5px 0 0;
  border: 1px solid ${(props) => props.theme.colors.scale[500]};
  color: ${(props) => props.theme.colors.background};
  /* o cabecalho acompanha a largura rolada, nao so a visivel: sem isso a
     faixa laranja corta no meio quando a tabela rola para o lado */
  min-width: max-content;
  ${TableItem} {
    span {
      padding: 8px 10px;
      font-weight: bold;
      font-size: 13px;
      letter-spacing: 0.2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const TableHeaderButton = styled.button`
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.background};
  transition: all ${(props) => props.theme.animation.delay.fast} ease;
  cursor: pointer;
  font-weight: bold;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    filter: grayscale(1);
    cursor: not-allowed;
    opacity: .5;
  }


  &[data-hidden="true"]{
      opacity:0;
}
  ${(props) =>
    props.inverse &&
    css`
      color: ${(props) => props.theme.colors.scale[500]};
    `}
`;

export const TableHeaderSortButton = styled.button`
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  gap: 4px;
  background: none;
  border: none;
  color: inherit;
  font-weight: bold;
  font-size: 13px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  transition: opacity ${(props) => props.theme.animation.delay.fast} ease;
  &:hover {
    opacity: 0.9;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  border-radius: 5px;
  flex: 1;
  /* colunas tem largura fixa em px; quando o menu lateral expande e a soma
     nao cabe, a tabela rola dentro da propria caixa em vez de estourar a pagina */
  overflow-x: auto;
  ${(props) =>
    props.config &&
    css`
      /*
       * Esta regra vem DEPOIS da que libera a celula com componente e tem a
       * mesma especificidade, entao vencia e o menu de acoes voltava a ser
       * cortado mesmo com data-livre. O :not() tira as celulas livres da
       * conta: texto continua recortando, componente passa.
       */
      ${TableItem}:not([data-livre="true"]) {
        overflow: hidden;
      }
    `}
`;
